import { validateAssetPowerConfiguration } from "./assetValidation";
import {
  type Portfolio,
  PortfolioDataSource,
  type PortfolioRow,
  PortfolioType,
  buildAssetPowerParameter,
  hasRequiredCoordinates,
  hasRequiredEnergyFields,
} from "./types";

export type PortfolioCoordinates = { lat: number; lon: number };
export type PortfolioPowerRequest = {
  assetId: string;
  coordinates: PortfolioCoordinates;
  parameter: string;
};

type AssetIssue = "missing-asset-id" | "duplicate-asset-id" | "unsupported-source" | "unsupported-technology";

export type ResolvedPortfolioAsset = {
  rowIndex: number;
  assetId: string | null;
  label: string;
  coordinates: PortfolioCoordinates | null;
  issue: AssetIssue | null;
  invalidFields: string[];
  powerRequest: PortfolioPowerRequest | null;
  siteId: string | null;
};

export function portfolioAssetLabel(asset: PortfolioRow) {
  return asset.assetName?.trim() || asset.assetId?.trim() || asset.id || "";
}

/** Resolve every row once; consumers choose which rows they can display or request. */
export function resolvePortfolioAssets(portfolio: Portfolio): ResolvedPortfolioAsset[] {
  const counts = new Map<string, number>();
  for (const asset of portfolio.assets) {
    if (asset.id?.trim()) {
      counts.set(asset.id, (counts.get(asset.id) ?? 0) + 1);
    }
  }
  return portfolio.assets.map((asset, rowIndex) => {
    const assetId = asset.id?.trim() ? asset.id : null;
    const coordinates = hasRequiredCoordinates(asset)
      ? { lat: asset.coordinates.lat, lon: asset.coordinates.lon }
      : null;
    let issue: AssetIssue | null = null;
    if (!assetId) {
      issue = "missing-asset-id";
    } else if (counts.get(assetId) !== 1) {
      issue = "duplicate-asset-id";
    } else if (
      portfolio.dataSource !== PortfolioDataSource.Api &&
      portfolio.dataSource !== PortfolioDataSource.Energy
    ) {
      issue = "unsupported-source";
    } else if (portfolio.portfolioType !== PortfolioType.Wind && portfolio.portfolioType !== PortfolioType.Solar) {
      issue = "unsupported-technology";
    }

    const invalidFields = issue
      ? []
      : portfolio.dataSource === PortfolioDataSource.Api
        ? [...(!coordinates ? ["coordinates"] : []), ...validateAssetPowerConfiguration(portfolio.portfolioType, asset)]
        : hasRequiredEnergyFields(asset)
          ? []
          : ["properties.site_id"];
    return {
      rowIndex,
      assetId,
      label: portfolioAssetLabel(asset),
      coordinates,
      issue,
      invalidFields,
      powerRequest:
        !issue && !invalidFields.length && assetId && coordinates && portfolio.dataSource === PortfolioDataSource.Api
          ? { assetId, coordinates, parameter: buildAssetPowerParameter(portfolio.portfolioType, asset, "MW") }
          : null,
      siteId:
        !issue && !invalidFields.length && portfolio.dataSource === PortfolioDataSource.Energy
          ? (asset.properties?.site_id as string).trim()
          : null,
    };
  });
}
